/**
 * Create Search Widget Iframe
 * Creates and manages the iframe containing the search widget
 */

import type { LoaderConfig } from './types';

export interface IframeAPI {
  element: HTMLIFrameElement;
  container: HTMLDivElement;
  show: () => void;
  hide: (onComplete?: () => void) => void;
  isVisible: () => boolean;
  focus: () => void;
  postMessage: (message: unknown) => void;
  destroy: () => void;
}

export function createIframe(widgetUrl: string, config: LoaderConfig, onLoad: () => void): IframeAPI {
  const isInlineMode = config.embedMode === 'inline';

  const container = document.createElement('div');
  container.className = isInlineMode ? 'savanto-search-inline' : 'savanto-search-container';
  container.setAttribute('data-sv-component', 'search-container');
  container.setAttribute('data-sv-mode', isInlineMode ? 'inline' : 'floating');

  if (isInlineMode) {
    // Inherit min-height from the host wrapper and use flex so the iframe fills
    // the container regardless of whether the wrapper is sized via `height` or
    // `min-height`. Without this, `height: 100%` on the iframe collapses when
    // the wrapper only specifies `min-height`.
    container.style.cssText = `
      position: relative;
      width: 100%;
      height: 100%;
      min-height: inherit;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    `;
  } else {
    container.style.cssText = `
      position: fixed;
      inset: 0;
      z-index: 999999;
      opacity: 0;
      transition: opacity 0.2s ease;
      pointer-events: none;
      display: none;
    `;
  }

  const iframe = document.createElement('iframe');
  iframe.className = 'savanto-search-iframe';
  iframe.setAttribute('title', 'Savanto Search Widget');
  iframe.setAttribute('data-sv-component', 'search-iframe');

  iframe.setAttribute(
    'sandbox',
    'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation',
  );

  iframe.style.cssText = `
    flex: 1 1 auto;
    width: 100%;
    height: 100%;
    min-height: inherit;
    border: none;
    background: transparent;
    display: block;
  `;

  iframe.onload = () => {
    onLoad();
  };

  iframe.src = widgetUrl;

  container.appendChild(iframe);

  if (isInlineMode && config.containerSelector) {
    const targetContainer = document.querySelector(config.containerSelector);
    if (targetContainer) {
      targetContainer.appendChild(container);
    } else {
      console.error(
        `[SavantoSearch] Container not found: "${config.containerSelector}". ` +
          'The inline search widget will not render. Ensure the element exists in the DOM before initializing.',
      );
      container.innerHTML =
        `<div style="padding:16px;color:#b91c1c;font-family:system-ui;font-size:14px;">` +
        `Search widget error: container "${config.containerSelector}" not found.</div>`;
      document.body.appendChild(container);
    }
  } else {
    document.body.appendChild(container);
  }

  let isVisible = isInlineMode;

  return {
    element: iframe,
    container,
    show: () => {
      if (isVisible) return;
      isVisible = true;

      if (isInlineMode) return;

      container.style.display = 'block';
      // Trigger reflow for animation
      container.offsetHeight;
      container.style.opacity = '1';
      container.style.pointerEvents = 'auto';
    },
    hide: (onComplete?: () => void) => {
      if (!isVisible) return;

      if (isInlineMode) {
        onComplete?.();
        return;
      }

      isVisible = false;
      container.style.opacity = '0';
      container.style.pointerEvents = 'none';
      setTimeout(() => {
        if (!isVisible) {
          container.style.display = 'none';
        }
        onComplete?.();
      }, 200);
    },
    isVisible: () => isVisible,
    focus: () => {
      // Move keyboard focus into the iframe frame so the widget's own input
      // autofocus can land. On reopen the host frame holds focus and the
      // widget's in-frame focus() can't pull it cross-origin. Focus both the
      // element (always allowed) and the window (focus() is cross-origin-safe).
      iframe.focus();
      iframe.contentWindow?.focus();
    },
    postMessage: (message: unknown) => {
      if (iframe.contentWindow) {
        iframe.contentWindow.postMessage(message, '*');
      }
    },
    destroy: () => {
      container.remove();
    },
  };
}
